<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useRoute, useRouter } from 'vue-router';
import DropdownSelect from 'dashboard/components-next/dropdown-select/DropdownSelect.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TeamNumberSearch from 'dashboard/components-next/TeamNumbers/TeamNumberSearch.vue';
import TeamNumbersAPI from 'dashboard/api/aceleres/teamNumbers';
import TeamNumberHistoryModal from './TeamNumberHistoryModal.vue';
import TeamNumberRemoveModal from './TeamNumberRemoveModal.vue';

const route = useRoute();
const router = useRouter();
const records = ref([]);
const counts = ref({ active: 0, inactive: 0, all: 0 });
const totalCount = ref(0);
const hasAcelerity = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);
const search = ref('');
const status = ref('active');
const origin = ref('all');
const sortOrder = ref('desc');
const pageFromQuery = value => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : 1;
};
const currentPage = ref(pageFromQuery(route.query.page));
const perPage = 20;
const historyContact = ref(null);
const openMenuId = ref(null);
const showInfo = ref(false);
const confirmation = ref(null);
const removeError = ref('');
const undoRecord = ref(null);
let searchDebounce;
let undoTimeout;

const accountId = computed(() => route.params.accountId);
const isPageActive = computed(() => route.name === 'team_numbers_list');
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalCount.value / perPage))
);
const statusTabs = [
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Removidos' },
  { value: 'all', label: 'Todos' },
];
const contactName = record => record.contact.name || 'Sem nome';
const initials = name =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
const contactIcon = record =>
  (record.contact.identifier || '').startsWith('instagram:')
    ? 'i-lucide-instagram'
    : 'i-lucide-message-circle';
const contactDetail = record => {
  if (record.contact.phone_number) return record.contact.phone_number;
  const identifier = record.contact.identifier || '';
  if (identifier.startsWith('instagram:')) return 'Instagram · sem telefone';
  if (identifier.startsWith('messenger:')) return 'Messenger · sem telefone';
  return 'Sem telefone';
};
const contactLink = record =>
  frontendURL(`accounts/${accountId.value}/contacts/${record.contact.id}`);
const formatDate = value =>
  value
    ? messageTimestamp(
        new Date(value).getTime() / 1000,
        'dd/MM/yyyy HH:mm'
      ).replace(' ', ' · ')
    : '—';
const originLabel = record =>
  record.manual_override ? 'Manual' : 'Automático';
const markedBy = record => {
  if (!record.manual_override) return 'Sincronização automática';
  if (record.status === 'inactive') {
    return (
      record.last_changed_by?.name ||
      record.manual_override_by?.name ||
      'Sistema'
    );
  }
  return record.marked_by?.name || record.manual_override_by?.name || 'Sistema';
};
const decisionDate = record =>
  record.status === 'inactive' ? record.unmarked_at : record.marked_at;
const fetchRecords = async () => {
  isLoading.value = true;
  try {
    const { data } = await TeamNumbersAPI.list({
      search: search.value || undefined,
      status: status.value,
      origin: origin.value,
      sort_by: 'marked_at',
      sort_order: sortOrder.value,
      page: currentPage.value,
      per_page: perPage,
    });
    records.value = data.payload || [];
    totalCount.value = data.meta?.total_count || 0;
    counts.value = data.meta?.counts || counts.value;
    hasAcelerity.value = data.meta?.has_acelerity === true;
    if (!hasAcelerity.value && origin.value !== 'all') origin.value = 'all';
    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value;
      router.replace({ query: { ...route.query, page: currentPage.value } });
      await fetchRecords();
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        'Não foi possível carregar os números da equipe.'
    );
  } finally {
    isLoading.value = false;
  }
};
const applyFilters = () => {
  currentPage.value = 1;
  if (route.query.page) router.replace({ query: { ...route.query, page: 1 } });
  fetchRecords();
};
const onPageChange = page => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return;
  currentPage.value = page;
  router.push({ query: { ...route.query, page } });
  fetchRecords();
};
const closeActionMenu = () => {
  openMenuId.value = null;
};
const toggleActionMenu = id => {
  openMenuId.value = openMenuId.value === id ? null : id;
  showInfo.value = false;
};
const handleDocumentClick = event => {
  if (!event.target.closest('[data-team-number-menu]')) closeActionMenu();
  if (!event.target.closest('[data-team-number-info]')) showInfo.value = false;
};
const handleKeydown = event => {
  if (event.key === 'Escape') {
    closeActionMenu();
    showInfo.value = false;
    if (!isSaving.value) confirmation.value = null;
    historyContact.value = null;
  }
};
const openHistory = record => {
  closeActionMenu();
  historyContact.value = record.contact;
};
const confirmAction = (type, record) => {
  closeActionMenu();
  removeError.value = '';
  confirmation.value = { type, record };
};
const remove = async record => {
  await TeamNumbersAPI.unmark(record.contact.id);
  undoRecord.value = record;
  clearTimeout(undoTimeout);
  undoTimeout = setTimeout(() => {
    undoRecord.value = null;
  }, 8000);
  fetchRecords();
};
const restore = async record => {
  closeActionMenu();
  try {
    await TeamNumbersAPI.mark(record.contact.id);
    clearTimeout(undoTimeout);
    undoRecord.value = null;
    useAlert('Adicionado à equipe');
    fetchRecords();
  } catch {
    useAlert('Não foi possível desfazer agora. Tente novamente.');
  }
};
const submitConfirmation = async () => {
  if (isSaving.value || !confirmation.value) return;
  const { type, record } = confirmation.value;
  if (type === 'remove') removeError.value = '';
  isSaving.value = true;
  try {
    if (type === 'remove') await remove(record);
    else {
      await TeamNumbersAPI.followAuto(record.contact.id);
      useAlert('Decisão devolvida ao sistema');
      fetchRecords();
    }
    confirmation.value = null;
  } catch (error) {
    if (type === 'remove') {
      removeError.value = 'Não foi possível remover agora. Tente novamente.';
    } else {
      useAlert(
        error?.response?.data?.message || 'Não foi possível concluir a ação.'
      );
    }
  } finally {
    isSaving.value = false;
  }
};
watch([status, origin, sortOrder], applyFilters);
watch(
  () => route.query.page,
  page => {
    const nextPage = pageFromQuery(page);
    if (nextPage !== currentPage.value) {
      currentPage.value = nextPage;
      fetchRecords();
    }
  }
);
watch(search, () => {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(applyFilters, 300);
});
onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleKeydown);
  fetchRecords();
});
onBeforeUnmount(() => {
  clearTimeout(searchDebounce);
  clearTimeout(undoTimeout);
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <SettingsLayout class="team-numbers-page">
    <template #header>
      <Teleport
        v-if="isPageActive"
        defer
        to=".settings-wrapper__teams-header-outlet"
      >
        <header
          class="team-numbers-page__header aceleres-team-numbers-page__header acl-mobile-account-menu-host !flex-wrap !gap-4 !pb-6"
        >
          <span
            class="team-numbers-page__header-icon relative !size-[52px] !rounded-2xl !text-[#111827] after:absolute after:bottom-3 after:right-3 after:size-1.5 after:rounded-full after:bg-red-500"
            ><Icon icon="i-lucide-shield-check"
          /></span>
          <div class="team-numbers-page__heading min-w-0 flex-1">
            <div class="team-numbers-page__heading-line">
              <h1 class="!text-[28px] max-[720px]:!text-[22px]">
                Números da equipe
              </h1>
              <div class="team-numbers-page__info-anchor" data-team-number-info>
                <button
                  type="button"
                  class="team-numbers-page__info-trigger !size-6 !bg-transparent !text-[#8c959f]"
                  title="Como um número entra aqui"
                  aria-label="Como um número entra aqui"
                  :aria-expanded="showInfo"
                  @click="showInfo = !showInfo"
                >
                  <Icon icon="i-lucide-info" class="size-4" />
                </button>
                <div v-if="showInfo" class="team-numbers-page__popover">
                  <strong>Como um número entra aqui?</strong>
                  <div>
                    <span
                      class="team-numbers-page__badge team-numbers-page__badge--manual"
                      ><Icon icon="i-lucide-user-round" />Manual</span
                    >
                    <p>Alguém da equipe marcou ou removeu o contato à mão.</p>
                  </div>
                  <div v-if="hasAcelerity">
                    <span
                      class="team-numbers-page__badge team-numbers-page__badge--auto"
                      ><Icon icon="i-lucide-refresh-cw" />Automático</span
                    >
                    <p>
                      O telefone é de um usuário cadastrado (vendedor, gestor…).
                      Disponível para empresas com Acelerity.
                    </p>
                  </div>
                  <footer v-if="hasAcelerity">
                    Se as duas regras discordarem, vale a decisão manual.
                  </footer>
                </div>
              </div>
            </div>
            <p>
              Contatos internos: conversam normalmente, mas a IA, o funil e as
              automações ignoram.
            </p>
          </div>
          <TeamNumberSearch
            v-model="search"
            class="team-numbers-page__search !h-11 !w-[360px] !flex-[0_0_360px] !rounded-xl !bg-white !px-4 !text-[#6b7280] shadow-sm max-[1100px]:!w-full max-[1100px]:!flex-[1_0_100%]"
          />
        </header>
      </Teleport>
    </template>
    <template #preBody>
      <div
        class="team-numbers-page__card min-h-[calc(100dvh-160px)] !rounded-[24px] max-[720px]:min-h-[calc(100dvh-230px)]"
      >
        <div
          class="team-numbers-page__filters !flex-wrap !min-h-[74px] !px-5 min-[721px]:!flex-row min-[721px]:!items-center max-[720px]:!gap-3"
        >
          <div
            class="team-numbers-page__tabs !gap-1 rounded-full bg-[#f5f5f3] p-1 [&>button]:!bg-transparent [&>button.is-active]:!bg-[#111827] [&_small]:inline-grid [&_small]:size-5 [&_small]:place-items-center [&_small]:rounded-full [&_small]:bg-[#eceef1] [&_small]:!opacity-100 [&>button.is-active_small]:bg-white/20"
            aria-label="Status dos números da equipe"
          >
            <button
              v-for="tab in statusTabs"
              :key="tab.value"
              type="button"
              :class="{ 'is-active': status === tab.value }"
              @click="status = tab.value"
            >
              {{ tab.label }} <small>{{ counts[tab.value] }}</small>
            </button>
          </div>
          <div class="team-numbers-page__filter-controls">
            <DropdownSelect
              v-if="hasAcelerity"
              v-model="origin"
              class="team-numbers-page__dropdown w-[180px] basis-[180px] shrink-0"
              role="group"
              aria-label="Quem decidiu"
              :options="[
                { value: 'all', label: 'Quem decidiu: Todos' },
                { value: 'manual', label: 'Manual' },
                { value: 'automatico', label: 'Automático' },
              ]"
            />
            <DropdownSelect
              v-model="sortOrder"
              class="team-numbers-page__dropdown w-[134px] basis-[134px] shrink-0"
              role="group"
              aria-label="Ordenação"
              :options="[
                { value: 'desc', label: 'Mais recentes' },
                { value: 'asc', label: 'Mais antigos' },
              ]"
            />
          </div>
        </div>
        <div
          class="team-numbers-page__table-head !gap-5 !px-7"
          :class="{
            'without-origin': !hasAcelerity,
            '!grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)_minmax(0,1.6fr)_36px] [&.without-origin]:!grid-cols-[minmax(0,2.3fr)_minmax(0,1.6fr)_36px]':
              status !== 'all',
            '!grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)_minmax(0,1.3fr)_90px_36px] [&.without-origin]:!grid-cols-[minmax(0,2.3fr)_minmax(0,1.3fr)_90px_36px]':
              status === 'all',
          }"
        >
          <span>Contato</span><span v-if="hasAcelerity">Quem decidiu</span
          ><span>Marcado por</span><span v-if="status === 'all'">Status</span
          ><span class="sr-only">Ações</span>
        </div>
        <div v-if="isLoading" class="team-numbers-page__empty">
          Carregando números da equipe...
        </div>
        <div v-else-if="!records.length" class="team-numbers-page__empty">
          Nenhum número encontrado.
        </div>
        <div v-else class="team-numbers-page__rows !px-3">
          <div
            v-for="record in records"
            :key="record.id"
            class="team-numbers-page__row !min-h-[74px] !rounded-2xl !grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)_minmax(0,1.6fr)_36px] !gap-5 !px-4 [&.without-origin]:!grid-cols-[minmax(0,2.3fr)_minmax(0,1.6fr)_36px] max-[720px]:!grid-cols-[minmax(0,1fr)_auto] max-[720px]:[&.without-origin]:!grid-cols-[minmax(0,1fr)_auto]"
            :class="{
              'is-open': openMenuId === record.id,
              'without-origin': !hasAcelerity,
              'min-[721px]:!grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)_minmax(0,1.3fr)_90px_36px] min-[721px]:[&.without-origin]:!grid-cols-[minmax(0,2.3fr)_minmax(0,1.3fr)_90px_36px]':
                status === 'all',
            }"
          >
            <router-link
              :to="contactLink(record)"
              class="team-numbers-page__contact"
            >
              <span class="team-numbers-page__avatar !size-[42px] !text-sm"
                ><img
                  v-if="record.contact.thumbnail"
                  :src="record.contact.thumbnail"
                  alt=""
                />{{
                  record.contact.thumbnail ? '' : initials(contactName(record))
                }}</span
              ><span class="team-numbers-page__contact-text"
                ><strong>{{ contactName(record) }}</strong
                ><small class="flex items-center gap-1.5"
                  ><Icon
                    :icon="contactIcon(record)"
                    class="size-3 shrink-0"
                  />{{ contactDetail(record) }}</small
                ></span
              >
            </router-link>
            <span
              v-if="hasAcelerity"
              class="team-numbers-page__badge !rounded-lg [&>span]:size-3 [&>span]:shrink-0"
              :class="
                record.manual_override
                  ? 'team-numbers-page__badge--manual'
                  : 'team-numbers-page__badge--auto'
              "
              ><Icon
                :icon="
                  record.manual_override
                    ? 'i-lucide-user-round'
                    : 'i-lucide-refresh-cw'
                "
              />{{ originLabel(record) }}</span
            >
            <div
              class="team-numbers-page__marked !flex-row items-center gap-2.5"
            >
              <span
                class="grid size-[26px] shrink-0 place-items-center rounded-full text-[10px] font-bold"
                :class="
                  record.manual_override
                    ? 'bg-[#111827] text-white'
                    : 'bg-[#e6eefc] text-[#1d4f91]'
                "
              >
                <template v-if="record.manual_override">{{
                  initials(markedBy(record))
                }}</template>
                <Icon v-else icon="i-lucide-refresh-cw" class="size-3" />
              </span>
              <span class="flex min-w-0 flex-col">
                <strong>{{ markedBy(record) }}</strong>
                <small>{{ formatDate(decisionDate(record)) }}</small>
              </span>
            </div>
            <span
              v-if="status === 'all'"
              class="team-numbers-page__status"
              :class="record.status === 'active' ? 'is-active' : 'is-removed'"
              ><i />{{
                record.status === 'active' ? 'Ativo' : 'Removido'
              }}</span
            >
            <div class="team-numbers-page__actions" data-team-number-menu>
              <button
                type="button"
                class="team-numbers-page__menu-trigger !size-9 !rounded-xl"
                :class="{ 'is-open': openMenuId === record.id }"
                :aria-label="`Ações de ${contactName(record)}`"
                :aria-expanded="openMenuId === record.id"
                aria-haspopup="menu"
                @click.stop="toggleActionMenu(record.id)"
              >
                <Icon icon="i-lucide-ellipsis" class="size-5" />
              </button>
              <div
                v-if="openMenuId === record.id"
                class="team-numbers-page__menu !top-[42px] !w-[304px] !rounded-2xl !p-1.5 !shadow-[0_20px_44px_#0002] [&>a]:!min-h-10 [&>button]:!min-h-10 [&>a>span]:size-[15px] [&>button>span:first-child]:size-[15px] [&>a>span]:shrink-0 [&>button>span:first-child]:shrink-0 max-[720px]:!w-[min(304px,calc(100vw-64px))]"
                role="menu"
              >
                <router-link
                  :to="contactLink(record)"
                  role="menuitem"
                  @click="closeActionMenu"
                >
                  <Icon icon="i-lucide-eye" />Ver contato
                </router-link>
                <button role="menuitem" @click="openHistory(record)">
                  <Icon icon="i-lucide-history" />Ver histórico
                </button>
                <button
                  v-if="hasAcelerity && record.manual_override"
                  role="menuitem"
                  class="team-numbers-page__menu-two-line !bg-[#f7f8f7] !py-2"
                  @click="confirmAction('auto', record)"
                >
                  <Icon
                    icon="i-lucide-refresh-cw"
                    class="team-numbers-page__menu-title-icon"
                  /><span class="team-numbers-page__menu-title-copy"
                    >Deixar o sistema decidir<small
                      >Volta a usar o cadastro de usuários para marcar ou
                      não</small
                    ></span
                  >
                </button>
                <hr />
                <button
                  v-if="record.status === 'active'"
                  role="menuitem"
                  class="team-numbers-page__danger"
                  @click="confirmAction('remove', record)"
                >
                  <Icon icon="i-lucide-circle-minus" />Remover da equipe
                </button>
                <button
                  v-else
                  role="menuitem"
                  class="team-numbers-page__success"
                  @click="restore(record)"
                >
                  <Icon icon="i-lucide-circle-plus" />Adicionar de volta à
                  equipe
                </button>
              </div>
            </div>
          </div>
        </div>
        <footer
          class="team-numbers-page__footer mt-auto !min-h-[46px] !px-7 !py-3"
        >
          <span
            >Exibindo <strong>{{ records.length }}</strong> de
            <strong>{{ totalCount }}</strong>
            {{ totalCount === 1 ? 'número' : 'números' }}
            {{
              status === 'all'
                ? ''
                : status === 'active'
                  ? totalCount === 1
                    ? 'ativo'
                    : 'ativos'
                  : totalCount === 1
                    ? 'removido'
                    : 'removidos'
            }}</span
          >
          <div v-if="totalPages > 1">
            <button
              :disabled="currentPage === 1"
              aria-label="Página anterior"
              @click="onPageChange(currentPage - 1)"
            >
              ‹</button
            ><span>Página {{ currentPage }} de {{ totalPages }}</span
            ><button
              :disabled="currentPage === totalPages"
              aria-label="Próxima página"
              @click="onPageChange(currentPage + 1)"
            >
              ›
            </button>
          </div>
        </footer>
      </div>
      <TeamNumberHistoryModal
        v-if="historyContact"
        :contact="historyContact"
        @close="historyContact = null"
      />
      <TeamNumberRemoveModal
        v-if="confirmation?.type === 'remove'"
        :contact-name="contactName(confirmation.record)"
        :has-acelerity="hasAcelerity"
        :is-saving="isSaving"
        :error="removeError"
        @close="confirmation = null"
        @confirm="submitConfirmation"
      />
      <Teleport to="body">
        <div
          v-if="confirmation?.type === 'auto'"
          class="team-numbers-page__backdrop fixed inset-0 z-[2147483550] grid place-items-center overflow-y-auto bg-[rgba(20,24,31,0.35)] p-4"
          @click.self="confirmation = null"
        >
          <section
            class="team-numbers-page__modal"
            role="dialog"
            aria-modal="true"
            aria-label="Deixar o sistema decidir"
          >
            <span class="team-numbers-page__modal-icon auto"
              ><Icon icon="i-lucide-refresh-cw"
            /></span>
            <h2>
              Deixar o sistema decidir sobre
              {{ contactName(confirmation.record) }}?
            </h2>
            <p>
              Hoje ele está aqui porque
              <strong
                >{{
                  confirmation.record.manual_override_by?.name ||
                  'alguém da equipe'
                }}
                {{
                  confirmation.record.status === 'active' ? 'marcou' : 'removeu'
                }}
                manualmente</strong
              >. Se você devolver a decisão ao sistema:
            </p>
            <div class="team-numbers-page__modal-box">
              <div>
                <b>1</b> Na próxima sincronização, o sistema confere o cadastro
                de usuários.
              </div>
              <div>
                <b>2</b> Se o telefone for de um usuário (vendedor, gestor…),
                ele continua na equipe.
              </div>
              <div>
                <b>3</b> Se não for, ele sai da lista sozinho e volta a ser
                cliente comum.
              </div>
            </div>
            <small
              >A mudança não é imediata: acontece na próxima
              sincronização.</small
            >
            <div class="team-numbers-page__modal-actions">
              <button @click="confirmation = null">Manter decisão manual</button
              ><button
                class="auto"
                :disabled="isSaving"
                @click="submitConfirmation"
              >
                Deixar o sistema decidir
              </button>
            </div>
          </section>
        </div>
      </Teleport>
      <div v-if="undoRecord" class="team-numbers-page__undo">
        {{ contactName(undoRecord) }} removido da equipe
        <button @click="restore(undoRecord)">Desfazer</button>
      </div>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.team-numbers-page {
  color: #1f2328;
  background: #f5f5f3;
  padding: 0 48px 36px;
}
.team-numbers-page__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 0 20px;
  background: #f5f5f3;
}
.team-numbers-page__header-icon {
  width: 48px;
  height: 48px;
  flex: none;
  display: grid;
  place-items: center;
  color: #15803d;
  border: 1px solid #eceef1;
  border-radius: 14px;
  background: #fff;
}
.team-numbers-page__header-icon :deep(span) {
  width: 22px;
  height: 22px;
}
.team-numbers-page__heading-line {
  display: flex;
  align-items: center;
  gap: 10px;
}
.team-numbers-page__heading h1 {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
}
.team-numbers-page__heading p {
  margin: 4px 0 0;
  font-size: 14px;
  color: #6b7280;
}
.team-numbers-page__total {
  padding: 3px 10px;
  border: 1px solid #eceef1;
  border-radius: 999px;
  background: #fff;
  color: #444c56;
  font-size: 12px;
  font-weight: 600;
}
.team-numbers-page__info-anchor {
  position: relative;
}
.team-numbers-page__info-trigger {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #e6eefc;
  color: #1d4f91;
}
.team-numbers-page__popover {
  position: absolute;
  top: 36px;
  left: 0;
  z-index: 40;
  width: 380px;
  display: grid;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 16px 40px #0002;
  font-size: 13px;
}
.team-numbers-page__popover strong {
  font-size: 14px;
}
.team-numbers-page__popover p {
  margin: 6px 0 0;
  color: #444c56;
  font-size: 13px;
}
.team-numbers-page__popover footer {
  border-top: 1px solid #f0f1f3;
  padding-top: 10px;
  color: #8c959f;
  font-size: 12px;
}
.team-numbers-page__card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #eceef1;
  border-radius: 20px;
  box-shadow:
    0 1px 3px #1018280a,
    0 8px 24px #1018280a;
  overflow: visible;
}
.team-numbers-page__filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f1f3;
}
.team-numbers-page__tabs,
.team-numbers-page__filter-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}
.team-numbers-page__tabs button {
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  background: #f5f5f3;
  color: #444c56;
  font-size: 13px;
  white-space: nowrap;
}
.team-numbers-page__tabs button.is-active {
  background: #1f2328;
  color: #fff;
  font-weight: 600;
}
.team-numbers-page__tabs small {
  margin-left: 5px;
  font-size: 11px;
  opacity: 0.7;
}
.team-numbers-page__search {
  flex: 0 0 320px;
  width: 320px;
  margin: 0;
  background: #f8fafc;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  height: 36px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 11px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #8c959f;
}
.team-numbers-page__search:focus-within {
  border-color: #94a3b8;
  background: #fff;
  box-shadow: 0 0 0 3px #0f172a0a;
}
.team-numbers-page__search :deep(span) {
  flex-shrink: 0;
  display: block;
}
.team-numbers-page__search input {
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  line-height: 20px;
  color: #444c56;
  min-width: 0;
  width: 100%;
  outline: 0;
  font-size: 13px;
  background: transparent;
}
.team-numbers-page__dropdown :deep(.contact-dropdown__trigger) {
  height: 36px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #444c56;
  font-size: 13px;
}
.team-numbers-page__dropdown :deep(.contact-dropdown__chevron) {
  display: block;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
.team-numbers-page__info-trigger,
.team-numbers-page__menu-trigger {
  padding: 0;
  border: 0;
  line-height: 1;
}
.team-numbers-page__info-trigger :deep(span),
.team-numbers-page__menu-trigger :deep(span) {
  display: block;
  margin: 0;
  line-height: 1;
}
.team-numbers-page__table-head,
.team-numbers-page__row {
  display: grid;
  grid-template-columns: 2.4fr 1.2fr 1.7fr 1fr 64px;
  gap: 20px;
  align-items: center;
}
.team-numbers-page__table-head.without-origin,
.team-numbers-page__row.without-origin {
  grid-template-columns: minmax(0, 2.4fr) minmax(0, 1.7fr) minmax(0, 1fr) 64px;
}
.team-numbers-page__table-head {
  padding: 12px 24px;
  color: #8c959f;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.team-numbers-page__table-head span:last-child {
  text-align: right;
}
.team-numbers-page__rows {
  padding: 0 8px;
}
.team-numbers-page__row {
  position: relative;
  min-height: 64px;
  padding: 0 16px;
  border-radius: 12px;
}
.team-numbers-page__row:hover,
.team-numbers-page__row.is-open {
  background: #f7f8f7;
}
.team-numbers-page__contact {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  color: #1f2328;
}
.team-numbers-page__avatar {
  width: 38px;
  height: 38px;
  flex: none;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  background: #e6eefc;
  color: #1d4f91;
  font-size: 13px;
  font-weight: 600;
}
.team-numbers-page__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.team-numbers-page__row:nth-child(4n + 2) .team-numbers-page__avatar {
  background: #e7f5ec;
  color: #166534;
}
.team-numbers-page__row:nth-child(4n + 3) .team-numbers-page__avatar {
  background: #fdebd8;
  color: #9a4b00;
}
.team-numbers-page__row:nth-child(4n + 4) .team-numbers-page__avatar {
  background: #f3e8fd;
  color: #6b21a8;
}
.team-numbers-page__contact-text,
.team-numbers-page__marked {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.team-numbers-page__contact-text strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}
.team-numbers-page__contact-text small,
.team-numbers-page__marked small {
  color: #8c959f;
  font-size: 12px;
}
.team-numbers-page__marked strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
}
.team-numbers-page__badge {
  display: inline-flex;
  width: max-content;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.team-numbers-page__badge :deep(svg) {
  width: 13px;
  height: 13px;
}
.team-numbers-page__badge--manual {
  background: #fff7e8;
  color: #9a4b00;
}
.team-numbers-page__badge--auto {
  background: #e6eefc;
  color: #1d4f91;
}
.team-numbers-page__status {
  display: inline-flex;
  width: max-content;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.team-numbers-page__status i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.team-numbers-page__status.is-active {
  background: #eef8f1;
  color: #166534;
}
.team-numbers-page__status.is-active i {
  background: #16a34a;
}
.team-numbers-page__status.is-removed {
  background: #f5f5f3;
  color: #6b7280;
}
.team-numbers-page__status.is-removed i {
  background: #c0c5cc;
}
.team-numbers-page__actions {
  position: relative;
  display: flex;
  justify-content: flex-end;
}
.team-numbers-page__menu-trigger {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: #57606a;
}
.team-numbers-page__menu-trigger.is-open,
.team-numbers-page__menu-trigger:hover {
  background: #eceef1;
}
.team-numbers-page__menu {
  position: absolute;
  right: 0;
  top: 38px;
  z-index: 30;
  width: 280px;
  padding: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 32px #0002;
}
.team-numbers-page__menu a,
.team-numbers-page__menu button {
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border-radius: 8px;
  text-align: left;
  color: #1f2328;
  font-size: 13px;
}
.team-numbers-page__menu a:hover,
.team-numbers-page__menu button:hover {
  background: #f7f8f7;
}
.team-numbers-page__menu :deep(svg) {
  width: 15px;
  height: 15px;
  flex: none;
}
.team-numbers-page__menu .team-numbers-page__menu-two-line {
  align-items: flex-start;
  padding-top: 5px !important;
  padding-bottom: 5px !important;
}
.team-numbers-page__menu-title-copy {
  line-height: 18px;
  display: flex;
  flex-direction: column;
}
.team-numbers-page__menu-title-icon {
  width: 15px;
  height: 15px;
  flex: none;
  margin-top: 2px;
}
.team-numbers-page__menu-two-line small {
  line-height: 16px;
  color: #6b7280;
  font-size: 11px;
}
.team-numbers-page__menu hr {
  margin: 5px 0;
  border-color: #eceef1;
}
.team-numbers-page__menu .team-numbers-page__danger {
  color: #b42318;
  font-weight: 600;
}
.team-numbers-page__menu .team-numbers-page__success {
  color: #15803d;
  font-weight: 600;
}
.team-numbers-page__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 50px;
  padding: 14px 24px;
  border-top: 1px solid #f0f1f3;
  color: #6b7280;
  font-size: 13px;
}
.team-numbers-page__footer div {
  display: flex;
  align-items: center;
  gap: 10px;
}
.team-numbers-page__footer button {
  font-size: 22px;
}
.team-numbers-page__footer button:disabled {
  opacity: 0.3;
}
.team-numbers-page__empty {
  padding: 48px 24px;
  text-align: center;
  color: #6b7280;
}
.team-numbers-page__modal {
  width: min(100%, 500px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 60px #0003;
}
.team-numbers-page__modal-icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 12px;
}
.team-numbers-page__modal-icon.auto {
  background: #e6eefc;
  color: #1d4f91;
}
.team-numbers-page__modal-icon :deep(span) {
  width: 20px;
  height: 20px;
}
.team-numbers-page__modal h2 {
  margin: 0;
  color: #1f2328;
  font-size: 18px;
  font-weight: 700;
}
.team-numbers-page__modal p {
  margin: 0;
  color: #57606a;
  font-size: 13px;
}
.team-numbers-page__modal-box {
  display: grid;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f7f8f7;
  color: #444c56;
  font-size: 13px;
}
.team-numbers-page__modal-box b {
  color: #1d4f91;
}
.team-numbers-page__modal > small {
  color: #8c959f;
  font-size: 12px;
}
.team-numbers-page__modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.team-numbers-page__modal-actions button {
  height: 40px;
  padding: 0 14px;
  border: 1px solid #d0d4da;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
}
.team-numbers-page__modal-actions button.auto {
  border-color: #1f2328;
  background: #1f2328;
  color: #fff;
}
.team-numbers-page__undo {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 60;
  padding: 12px 16px;
  border-radius: 10px;
  background: #1f2328;
  color: #fff;
  font-size: 13px;
  box-shadow: 0 12px 32px #0003;
}
.team-numbers-page__undo button {
  margin-left: 12px;
  text-decoration: underline;
  font-weight: 700;
}
@media (max-width: 1100px) {
  .team-numbers-page__filters {
    align-items: flex-start;
    flex-direction: column;
  }
  .team-numbers-page__filter-controls {
    flex-wrap: wrap;
  }
  .team-numbers-page__table-head,
  .team-numbers-page__row {
    gap: 10px;
  }
}
@media (max-width: 720px) {
  .team-numbers-page {
    padding: 0 16px 20px;
  }
  .team-numbers-page__header {
    align-items: flex-start;
  }
  .team-numbers-page__heading h1 {
    font-size: 22px;
  }
  .team-numbers-page__heading p {
    font-size: 12px;
  }
  .team-numbers-page__popover {
    left: auto;
    right: -20px;
    width: min(380px, calc(100vw - 32px));
  }
  .team-numbers-page__filter-controls {
    width: 100%;
  }
  .team-numbers-page__search {
    flex: 1 0 100%;
    width: 100%;
  }
  .team-numbers-page__table-head {
    display: none;
  }
  .team-numbers-page__row,
  .team-numbers-page__row.without-origin {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    padding: 12px;
  }
  .team-numbers-page__contact {
    grid-column: 1;
  }
  .team-numbers-page__row > .team-numbers-page__badge,
  .team-numbers-page__marked,
  .team-numbers-page__status {
    grid-column: 1;
  }
  .team-numbers-page__actions {
    grid-column: 2;
    grid-row: 1;
  }
  .team-numbers-page__menu {
    right: 0;
  }
  .team-numbers-page__footer {
    gap: 8px;
    flex-wrap: wrap;
  }
}
</style>
